import React, { useState } from "react";
import { CRMButtonPrimary } from "../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonSecondary } from "../CRMButtonSecondary/CRMButtonSecondary";
import { CRMCardOutsidePopupBlank } from "../CRMCardOutsidePopupBlank/CRMCardOutsidePopupBlank";
import { CRMPadding } from "../Simple/CRMPadding/CRMPadding";
import { CRMSpacer } from "../CRMSpacer/CRMSpacer";
import { CRMSpacingColumn } from "../CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../CRMSpacingRow/CRMSpacingRow";
import { CRMTitleForCard } from "../CRMTitleForCard/CRMTitleForCard";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";
import CRMInputLabelAndErrorWrapComponent from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { TListingRoadblocksListItem } from "../../../../domain/codecs/form/ListingRoadblockForm";
import { TListingStatusDisplayString } from "../../../../domain/codecs/ListingStatus";
import { TFormStatus } from "../../../../shared/src/codecs/codec";

export interface ICRMListingGoLiveControlProps {
    listingId: string;
    // Typed, not `string`: the whole control hinges on one literal comparison, so a renamed
    // display string must fail the build rather than silently hide the button for good.
    currentStatus: TListingStatusDisplayString;
    roadblocks: Array<TListingRoadblocksListItem>;
    // The roadblocks form is only fetched by the Roadblocks tab, so on a normal page load it is
    // empty. Without asking for it here the popup cannot tell "no checklist" from "not fetched"
    // and tells staff the listing has no checklist when it has one.
    roadblocksStatus: TFormStatus;
    liveDate: string | null;
    disabled: boolean;
    onLoadRoadblocks: (listingId: string) => void;
    onTakeLive: (payload: { listing_id: string; live_date: string }) => void;
}

export const CRMListingGoLiveControl = (props: ICRMListingGoLiveControlProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasAsked, setHasAsked] = useState(false);
    const [liveDate, setLiveDate] = useState<string>(new Date().toISOString().slice(0, 10));

    const isChecking = !hasAsked || props.roadblocksStatus === "loading";
    const marketPrep = props.roadblocks.find((r) => r.listing_roadblock_type === "market_prep_ready");

    // `live_on_portals` is declared last, so it being next means everything ahead is done.
    // `unresolved_count` would be wrong: it counts live_on_portals, which this button resolves.
    const isReady = marketPrep?.next_item?.type === "live_on_portals";
    const outstandingBeforeGoingLive = Math.max((marketPrep?.unresolved_count ?? 1) - 1, 0);

    const submit = () => {
        if (liveDate === "") {
            return;
        }

        props.onTakeLive({ listing_id: props.listingId, live_date: liveDate });
        setIsOpen(false);
    };

    // Replaces the free `live_date` input, so it still has to show the date once a listing is
    // live — read-only, because editing it here is the bypass this control exists to close.
    if (props.currentStatus !== "Instructed: needs market prep") {
        return (
            <CRMInputLabelAndErrorWrapComponent label="Live date">
                <CRMParagraph>
                    {props.liveDate === null
                        ? "Not live yet. A listing goes live from Market prep ready, once its checklist is done."
                        : props.liveDate}
                </CRMParagraph>
            </CRMInputLabelAndErrorWrapComponent>
        );
    }

    return (
        <CRMInputLabelAndErrorWrapComponent label="Live date">
            <CRMButtonPrimary
                label="Go live"
                disabled={props.disabled}
                onClick={() => {
                    props.onLoadRoadblocks(props.listingId);
                    setHasAsked(true);
                    setIsOpen(true);
                }}
            />

            <CRMCardOutsidePopupBlank
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                context={isReady ? "important" : "warning"}
            >
                <CRMPadding size="medium">
                    <CRMTitleForCard>
                        {isChecking ? "Go live" : isReady ? "Go live" : "Not ready to go live"}
                    </CRMTitleForCard>
                    <CRMSpacer size="tiny" />

                    {isChecking && (
                        <CRMParagraph>
                            Checking the market prep checklist…
                        </CRMParagraph>
                    )}

                    {!isChecking && marketPrep === undefined && (
                        <CRMParagraph>
                            This listing has no market prep checklist, so it cannot be taken live yet.
                        </CRMParagraph>
                    )}

                    {!isChecking && marketPrep !== undefined && !isReady && (
                        <CRMSpacingColumn spacing="tiny">
                            <CRMParagraph>
                                {outstandingBeforeGoingLive === 1
                                    ? "There is 1 thing left to do on Market prep ready before this property can go on the market."
                                    : `There are ${outstandingBeforeGoingLive} things left to do on Market prep ready before this property can go on the market.`}
                            </CRMParagraph>
                            {marketPrep.next_item !== null && (
                                <CRMParagraph>
                                    Next up: <strong>{marketPrep.next_item.display_name}</strong>. Open the Market prep ready roadblock to see the rest.
                                </CRMParagraph>
                            )}
                        </CRMSpacingColumn>
                    )}

                    {!isChecking && isReady && (
                        <>
                            <CRMParagraph>
                                Everything on Market prep ready is done. Going live puts the property in front of buyers and starts its days-on-market.
                            </CRMParagraph>
                            <CRMSpacer size="medium" />
                            <CRMInputLabelAndErrorWrapComponent label="Live date">
                                <input
                                    id="go-live-date"
                                    type="date"
                                    value={liveDate}
                                    onChange={(e) => setLiveDate(e.target.value)}
                                />
                            </CRMInputLabelAndErrorWrapComponent>
                        </>
                    )}

                    <CRMSpacer size="medium" />
                    <CRMSpacingRow spacing="medium" justifyContent="end">
                        <CRMButtonSecondary
                            label={!isChecking && isReady ? "Cancel" : "Close"}
                            onClick={() => setIsOpen(false)}
                        />
                        {!isChecking && isReady && (
                            <CRMButtonPrimary
                                label="Go live"
                                disabled={liveDate === ""}
                                onClick={submit}
                            />
                        )}
                    </CRMSpacingRow>
                </CRMPadding>
            </CRMCardOutsidePopupBlank>
        </CRMInputLabelAndErrorWrapComponent>
    );
};
